import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import LazyLoad from "react-lazyload";
import CTASection from "../../components/CTASection";
import DynamicEvents from "../../components/DynamicEvents";
import Youth1 from "../../assets/images/volunteer/16youth.webp";
import Youth2 from "../../assets/images/volunteer/18youth.webp";
import Youth3 from "../../assets/images/volunteer/underyouth.webp";
import Youth4 from "../../assets/images/volunteer/youth.webp";

const YouthPrograms = () => {
  return (
    <Container>
      <Helmet>
        <title>Youth Programs - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Join Habitat Buffalo's youth programs and help shape the future of your community. Engage in various volunteer opportunities designed for different age groups."
        />
      </Helmet>
      <h1>Youth Programs</h1>
      <LazyLoad height={200} offset={100}>
        <HeaderImage
          src={Youth4}
          alt="Youth volunteering at Habitat for Humanity Buffalo"
          loading="lazy"
        />
      </LazyLoad>
      <h2 className="section-header primary">Invest in the Future!</h2>
      <p className="text-center">
        Habitat Buffalo provides opportunities for youth to learn, grow, and
        make a tangible difference. Whether through hands-on volunteering or
        creative projects, our Youth Programs inspire young people to build a
        better future.
      </p>

      <Section>
        <h2 className="section-header green">Why Volunteer with Habitat?</h2>
        <p>Volunteering with Habitat Buffalo offers many benefits:</p>
        <ul>
          <li>
            Collaborate with diverse individuals, including future homeowners.
          </li>
          <li>
            Understand the challenges of unsafe housing versus affordable
            housing.
          </li>
          <li>Strengthen neighborhoods and communities.</li>
          <li>Engage in meaningful work and feel a sense of accomplishment.</li>
          <li>Learn new skills and gain confidence.</li>
          <li>Make a lasting impact—and have fun!</li>
        </ul>
      </Section>

      <Section>
        <h2 className="section-header green">Volunteers Ages 16-18</h2>
        <LazyLoad height={200} offset={100}>
          <SectionImage
            src={Youth1}
            alt="Youth volunteers aged 16-18"
            loading="lazy"
          />
        </LazyLoad>
        <p>
          Teens aged 16-18 can participate in a variety of tasks, including:
        </p>
        <ul>
          <li>
            <strong>Build Site Work:</strong> Painting, landscaping, site
            clean-up, and organizing materials (no power tools).
          </li>
          <li>
            <strong>ReStore Locations:</strong> Customer service, stocking
            shelves, sorting donations, assisting with sales, and cleaning.
          </li>
          <li>
            <strong>Office Work:</strong> Data entry, marketing support, and
            social media content creation.
          </li>
          <li>
            <strong>Community Cleanups:</strong> Neighborhood beautification,
            planting trees, and recycling events.
          </li>
        </ul>
      </Section>

      <Section>
        <h2 className="section-header orange">Volunteers Ages 14-16</h2>
        <LazyLoad height={200} offset={100}>
          <SectionImage
            src={Youth2}
            alt="Youth volunteers aged 14-16"
            loading="lazy"
          />
        </LazyLoad>
        <p>Youths aged 14-16 can assist with light tasks such as:</p>
        <ul>
          <li>
            <strong>ReStore Locations:</strong> Sorting donations, light
            administrative tasks, and merchandising.
          </li>
          <li>
            <strong>Office Work:</strong> Preparing mailings and assisting with
            event preparations.
          </li>
          <li>
            <strong>Community Cleanups:</strong> Trash collection and event
            support.
          </li>
        </ul>
      </Section>

      <Section>
        <h2 className="section-header primary">Volunteers Under 14</h2>
        <LazyLoad height={200} offset={100}>
          <SectionImage
            src={Youth3}
            alt="Youth volunteers under 14"
            loading="lazy"
          />
        </LazyLoad>
        <p>
          Younger children can get involved through creative and fundraising
          activities such as:
        </p>
        <ul>
          <li>
            <strong>Fundraising Campaigns:</strong> Participate in unique
            campaigns like jar donations and bake sales.
          </li>
          <li>
            <strong>Creative Projects:</strong> Craft thank-you cards, design
            posters, and create care packages.
          </li>
          <li>
            <strong>Community Participation:</strong> Help with donation drives
            or youth-friendly events.
          </li>
        </ul>
      </Section>

      <CTASection
        title="Get Involved"
        description="Ready to make a difference? Join our youth programs today and help shape the future of your community."
        buttonText="Contact Us To Get Started"
        buttonLink="/form/youthProgram"
      />
      <DynamicEvents category="youth" categoryText="Youth" />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const HeaderImage = styled.img`
  width: 100%;
  max-width: 800px;
  margin: 20px auto;
  display: block;
  border-radius: 8px;

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 15px auto;
  }

  @media (max-width: 480px) {
    margin: 10px auto;
  }
`;

const Section = styled.div`
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const SectionImage = styled.img`
  width: 100%;
  max-width: 600px;
  margin: 20px auto;
  display: block;
  border-radius: 8px;

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 15px auto;
  }

  @media (max-width: 480px) {
    margin: 10px auto;
  }
`;

export default YouthPrograms;
